import { Skeleton } from '@mui/material'
import Typography from '@mui/material/Typography'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import styles from './ClubOffers.module.scss'
import routes from '../../../app/routes'
import CatalogList from '../../../components/CatalogList'
import CRE from '../../../components/CRE/CRE'
import ErrorBoundary from '../../../components/ErrorBoundary/ErrorBoundary'
import FormattedMessage from '../../../components/FormattedMessage'
import PageLayout from '../../../components/PageLayout'
import Pagination, { PaginationModeEnum } from '../../../components/Pagination'
import ProductFilter from '../../../components/ProductFilter/ProductFilter'
import useClubContext from '../../../hooks/club/clubContext'
import { useClubOffers } from '../../../hooks/clubOffers/clubOffers'
import { RESULTS_PER_PAGE } from '../../../hooks/search'
import CatalogInterface from '../../../interfaces/CatalogInterface'
import { ActiveFiltersInterface } from '../../../interfaces/FiltersInterface'
import * as creApi from '../../../services/cre/cre.api'
import { CREAppType, CREAppTypeEnum } from '../../../services/cre/cre.types'
import { State } from '../../../services/reducers'

const ClubOffers = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [creApp, setCreApp] = useState<CREAppType | null>(null)

  const clubContext = useClubContext()
  const language = useSelector((store: State) => store.locale.language)

  useEffect(() => {
    const fetchBanner = async () => {
      if (!clubContext.club?.id) return
      try {
        const res = await creApi.readCREBannerByContext({
          type: CREAppTypeEnum.BANNER_CLUB,
          bannerRefIds: [clubContext.club.id],
          language: language.isoCode,
        })

        if (res?.data?.cre?.content) {
          setCreApp(res.data)
          return
        }

        setCreApp(null)
      } catch (err) {
        setCreApp(null)
      }
    }
    
    fetchBanner()
  }, [clubContext.club?.id, language.isoCode])

  const {
    updateQueryString,
    fetchData,
    fetchCallback,
    dataTransformer,
    items,
    filters,
    activeFilters,
    areFiltersVisible,
    filtersQueryString,
    itemsCounter,
  } = useClubOffers()

  const handleFiltersChange = (newFilters: ActiveFiltersInterface) => {
    updateQueryString(newFilters)
  }

  if (!clubContext.isInClubFlow) {
    return <Redirect to={routes.home} />
  }

  return (
    <PageLayout>
      <ErrorBoundary>
        <div>
          {creApp ? (
            <div className={styles['club-offers__banner-container']}>
              <CRE item={creApp} />
            </div>
          ) : null}
          <Typography variant="h1" align="center">
            {!isLoading && items.length === 0 ? (
              <div className={styles['no-products__container']}>
                <FormattedMessage
                  id="Club.Offers.ProductsNotFound"
                  defaultMessage="There are currently no products in the shop. Check back later when it is stocked again!"
                  className={styles['no-products__label']}
                />
              </div>
            ) : null}
          </Typography>
          <div className={styles['club-offers__control-section']}>
            <ErrorBoundary show={false}>
              {areFiltersVisible ? (
                <ProductFilter
                  activeFilters={activeFilters}
                  filters={filters!}
                  loading={isLoading}
                  itemsCounter={itemsCounter}
                  onFilterChange={handleFiltersChange}
                />
              ) : (
                <Skeleton classes={{ root: styles['catalog-page__control-section__filters-skeleton'] }} />
              )}
            </ErrorBoundary>
          </div>
          {isLoading || items.length > 0 ? (
            <div>
              <Pagination<CatalogInterface, {}>
                mode={PaginationModeEnum.INFINITE}
                fetchData={fetchData}
                dataTransformer={dataTransformer}
                ResultsComponent={CatalogList}
                resultsPerPage={RESULTS_PER_PAGE}
                key={filtersQueryString}
                fetchCallback={fetchCallback}
                onLoading={setIsLoading}
                showLoader
                skipDefaultError
                resultsProps={{
                  isLoading,
                }}
                initItems={Array(RESULTS_PER_PAGE).fill(undefined)}
              />
            </div>
          ) : null}
        </div>
      </ErrorBoundary>
    </PageLayout>
  )
}

export default ClubOffers
